// ═══════════════════════════════════════════════════════════════════════════════
//  src/dojo/DojoAmbient.jsx
//  EL DOJO — capa ambiental
//
//  Partículas que titilan y alguna estrella fugaz esporádica. Es el mismo
//  recurso que usa FLUENCY NEXUS (`MiniGameModal.jsx`) y hace más de lo que
//  parece: da profundidad a un fondo plano y, sobre todo, mantiene la pantalla
//  VIVA en los segundos muertos — mientras Spacy habla, mientras el jefe lee
//  tu respuesta. Una pantalla quieta se siente colgada.
//
//  Se implementa en canvas y no con 65 divs animados por CSS a propósito: un
//  canvas es una sola capa que compone en GPU. Sesenta y cinco nodos animados
//  serían sesenta y cinco recálculos de estilo por frame en un móvil de gama
//  media, justo mientras corre el reconocedor de voz.
//
//  Se apaga entero con `prefers-reduced-motion`: no es decoración negociable,
//  es movimiento de fondo, y para quien lo tiene desactivado es ruido.
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';

const PARTICLES = 55;

// El cielo estrellado sólo existe en oscuro. Sobre el gris claro de NSL, un
// punto luminoso no es atmósfera: es una mancha. En claro el lienzo no se
// dibuja siquiera — cero trabajo de GPU, no sólo cero píxeles.
const P_RGB = '130,130,255';   // partícula
const S_RGB = '210,210,255';   // estela de la fugaz

const isDark = () =>
  document.documentElement.getAttribute('data-theme') === 'dark' ||
  document.body.getAttribute('data-theme') === 'dark';

export default function DojoAmbient() {
  const ref = useRef(null);

  // Si el alumno cambia de tema estando aquí, el lienzo tiene que enterarse:
  // comprobar el tema una sola vez al montar dejaba las estrellas encendidas
  // sobre el fondo claro hasta recargar la página.
  const [dark, setDark] = useState(isDark);
  useEffect(() => {
    const obs = new MutationObserver(() => setDark(isDark()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    obs.observe(document.body, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (reduced) return undefined;
    if (!dark) return undefined;

    const ctx = canvas.getContext('2d');
    let animId = 0;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // ── Partículas con titileo ────────────────────────────────────────────
    const pts = Array.from({ length: PARTICLES }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      r: Math.random() * 2 + 0.5,
      dx: (Math.random() - 0.5) * 0.26,
      dy: (Math.random() - 0.5) * 0.26,
      alpha: Math.random() * 0.32 + 0.1,
      tw: Math.random() * 0.022 + 0.006,
      phase: Math.random() * Math.PI * 2,
    }));

    // ── Estrellas fugaces: una a la vez, con enfriamiento largo ───────────
    let stars = [];
    let cooldown = 0;

    const spawn = () => {
      const angle = (Math.PI * 3) / 4 + (Math.random() - 0.5) * 0.4;
      const speed = Math.random() * 5 + 7;
      stars.push({
        x: canvas.width * 0.55 + Math.random() * canvas.width * 0.5,
        y: Math.random() * canvas.height * 0.4 - 40,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        len: Math.random() * 130 + 80,
        op: 0,
        maxOp: Math.random() * 0.26 + 0.16,
        fading: false,
      });
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      pts.forEach((p) => {
        p.phase += p.tw;
        const t = 0.65 + Math.sin(p.phase) * 0.35;
        const a = p.alpha * t;
        const r = p.r * (0.75 + t * 0.45);

        ctx.shadowColor = `rgba(${P_RGB}, ${a * 0.7})`;
        ctx.shadowBlur = r * 4;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${P_RGB}, ${a})`;
        ctx.fill();

        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      });
      ctx.shadowBlur = 0;

      cooldown -= 1;
      if (cooldown <= 0 && stars.length === 0 && Math.random() < 0.004) {
        spawn();
        cooldown = Math.floor(Math.random() * 140 + 280);   // ~5-7 s
      }

      stars.forEach((s) => {
        if (!s.fading) {
          s.op += 0.035;
          if (s.op >= s.maxOp) { s.op = s.maxOp; s.fading = true; }
        } else {
          s.op -= 0.006;
        }

        const grad = ctx.createLinearGradient(s.x, s.y, s.x - s.vx * (s.len / 10), s.y - s.vy * (s.len / 10));
        grad.addColorStop(0, `rgba(${S_RGB}, ${Math.max(0, s.op)})`);
        grad.addColorStop(1, `rgba(${S_RGB}, 0)`);

        ctx.strokeStyle = grad;
        ctx.lineWidth = 0.9;
        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(s.x - s.vx * (s.len / 10), s.y - s.vy * (s.len / 10));
        ctx.stroke();

        s.x += s.vx;
        s.y += s.vy;
      });
      stars = stars.filter((s) => s.op > 0 && s.x > -220 && s.y < canvas.height + 140);

      animId = requestAnimationFrame(draw);
    };

    animId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, [dark]);

  return <canvas ref={ref} className="dj-canvas-bg" aria-hidden="true" />;
}
